import { useMemo } from 'react'
import { useSetting } from './useSetting'
import { useTransactions } from './useTransactions'
import { useAppSymbols } from './useAppSymbols'
import { aggregateDailyPL, localDateKey } from '../lib/pl'
import { useStartOfDayEquity } from './useStartOfDayEquity'

export const MAX_GAIN_KEY = 'max-gain-per-day'

export type MaxGainType = 'dollar' | 'percent'

export interface MaxGainSettings {
  type: MaxGainType
  value: number | null
  enabled: boolean
}

export interface MaxGainLimitStatus {
  /** Today's realized P/L (signed; ≥ 0 means a gain). */
  dailyGain: number
  /** Today's P/L as percent of start-of-day account equity, or null when unknown. */
  dailyPct: number | null
  /** The selected limit type (dollar or percent). */
  type: MaxGainType | null
  /** The selected limit value. */
  value: number | null
  /** True when enabled and the gain limit is reached. Blocks NEW opens only. */
  capReached: boolean
  /** Short reason for banners/tooltips. */
  capReason: string | null
}

function parseSettings(raw: unknown): MaxGainSettings {
  if (typeof raw === 'object' && raw != null && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    const type = (obj.type === 'dollar' || obj.type === 'percent') ? obj.type : 'dollar'
    const value = typeof obj.value === 'number' && obj.value > 0 ? obj.value : null
    return {
      type,
      value: value ?? null,
      enabled: obj.enabled === true,
    }
  }
  return { type: 'dollar', value: null, enabled: false }
}

export function useMaxGainLimit(): MaxGainLimitStatus {
  const [raw] = useSetting<unknown>(MAX_GAIN_KEY, null)
  const settings = useMemo(() => parseSettings(raw), [raw])
  const txQuery = useTransactions()
  const { appSymbols } = useAppSymbols()
  const startEquity = useStartOfDayEquity()

  const dailyGain = useMemo(() => {
    const daily = aggregateDailyPL(txQuery.data ?? [], { symbolFilter: appSymbols })
    const todayKey = localDateKey(new Date().toISOString())
    const day = daily.get(todayKey)
    return day ? day.pl : 0
  }, [txQuery.data, appSymbols])

  const dailyPct =
    startEquity != null && startEquity > 0 ? (dailyGain / startEquity) * 100 : null

  const capReached =
    settings.enabled &&
    settings.value != null &&
    settings.value > 0 &&
    (settings.type === 'percent'
      ? dailyPct != null && dailyPct >= settings.value
      : dailyGain >= settings.value)

  const capReason = capReached
    ? settings.type === 'percent'
      ? `Max daily gain reached (${(dailyPct ?? 0).toFixed(2)}% / ${settings.value}%)`
      : `Max daily gain reached ($${dailyGain.toFixed(0)} / $${settings.value})`
    : null

  return {
    dailyGain,
    dailyPct,
    type: settings.enabled && settings.value != null ? settings.type : null,
    value: settings.enabled && settings.value != null ? settings.value : null,
    capReached,
    capReason,
  }
}
